import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Field, Icon, PageHeader, SelectField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { useCourses } from "../hooks/useCourses";
import { useFocusTimer } from "../focus/FocusTimerProvider";
import type { AmbienceKind } from "../focus/sounds";
import type { ProgressStats } from "@nelurio/contracts";
import { FocusCharts } from "../components/FocusCharts";
import { rememberDuration } from "../widgets/DashboardWidgets";
import { TREE_CATALOG, TreeKind, readSelectedTree, storeSelectedTree } from "../components/treeCatalog";
import type { Forest3DHandle, ForestTree } from "../forest3d/types";

type ForestRange = "day" | "week" | "month" | "term";

interface ForestDay { date: string; trees: Array<{ kind: string; count: number }> }
interface ForestData { days: ForestDay[]; trees: number; minutes: number; streak: number }

/** Growth stage thresholds by focus sessions (proposal: 1 = 0–1 · 2 = 2–3 · 3 = 4–7 · 4 = 8–13 · 5 = 14+). */
function stageForSessions(sessions: number): number {
  if (sessions >= 14) return 5;
  if (sessions >= 8) return 4;
  if (sessions >= 4) return 3;
  if (sessions >= 2) return 2;
  return 1;
}

/** Feature-detect a usable WebGL context before paying for the three.js chunk. */
function webglSupported(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(window.WebGLRenderingContext && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch {
    return false;
  }
}

function rangeBounds(range: ForestRange): { from: string; to: string } {
  const toKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const now = new Date();
  const to = toKey(now);
  if (range === "day") return { from: to, to };
  if (range === "week") {
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    return { from: toKey(from), to };
  }
  if (range === "month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toKey(from), to };
  }
  // term: last 6 months
  const from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  return { from: toKey(from), to };
}

const presets = [15, 25, 45, 60];
const MIN_MINUTES = 5;
const MAX_MINUTES = 120;

interface PlantedTree {
  id: string;
  kind: TreeKind;
  slot: number;
}

export function FocusPage() {
  const { t, formatNumber } = useI18n();
  const { courses } = useCourses();
  const focus = useFocusTimer();
  const [duration, setDuration] = useState(focus.running ? focus.durationMinutes : 25);
  const [courseId, setCourseId] = useState("");
  const [goal, setGoal] = useState("");
  const [stats, setStats] = useState<ProgressStats | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [treeKind, setTreeKind] = useState<TreeKind>(() => readSelectedTree());
  const sceneRef = useRef<HTMLDivElement>(null);
  // True 3D forest: the CSS island renders first and stays as the fallback path.
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const forest3dRef = useRef<Forest3DHandle | null>(null);
  const forestTreesRef = useRef<ForestTree[]>([]);
  const [forestMode, setForestMode] = useState<"css" | "3d">("css");

  useEffect(() => { if (courses.length && !courses.some((course) => course.id === courseId)) setCourseId(courses[0].id); }, [courses, courseId]);

  const loadStats = () => { if (!isDemoMode()) api<ProgressStats>("/progress").then(setStats).catch(() => undefined); };
  useEffect(loadStats, []);
  useEffect(() => { if (focus.lastSavedAt) { setSavedNotice(true); loadStats(); const timer = window.setTimeout(() => setSavedNotice(false), 4500); return () => window.clearTimeout(timer); } }, [focus.lastSavedAt]);

  const onSceneMove = (event: React.MouseEvent) => {
    const el = sceneRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const px = (event.clientX - box.left) / box.width - 0.5;
    const py = (event.clientY - box.top) / box.height - 0.5;
    el.style.setProperty("--ry", `${(px * 8).toFixed(2)}deg`);
    el.style.setProperty("--rx", `${(-py * 5).toFixed(2)}deg`);
  };
  const onSceneLeave = () => {
    const el = sceneRef.current;
    if (!el) return;
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--rx", "0deg");
  };

  const changeDuration = (delta: number) => setDuration((value) => Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, value + delta)));

  const mm = String(Math.floor(focus.secondsLeft / 60)).padStart(2, "0");
  const ss = String(focus.secondsLeft % 60).padStart(2, "0");
  const totalSeconds = focus.durationMinutes * 60;
  const progress = totalSeconds ? 1 - focus.secondsLeft / totalSeconds : 0;
  const sessions = stats?.focus.sessions ?? 0;
  const minutes = stats ? Math.round(stats.focus.totalSeconds / 60) : 0;

  const [forestRange, setForestRange] = useState<ForestRange>("week");
  const [forestData, setForestData] = useState<ForestData | null>(null);

  useEffect(() => {
    if (isDemoMode()) { setForestData({ days: [], trees: 0, minutes: 0, streak: 0 }); return; }
    const { from, to } = rangeBounds(forestRange);
    api<ForestData>(`/forest?from=${from}&to=${to}`).then(setForestData).catch(() => setForestData(null));
  }, [forestRange, focus.lastSavedAt]);

  // Trees of the selected range spread across the island slots (newest last).
  const planted = useMemo<PlantedTree[]>(() => {
    const all = (forestData?.days ?? []).flatMap((day) =>
      day.trees.flatMap((tree) => Array.from({ length: Math.min(tree.count, 4) }, () => ({ kind: tree.kind, date: day.date })))
    );
    const recent = all.slice(-12);
    return recent.map((tree, index) => ({
      id: `${tree.date}-${index}`,
      kind: (TREE_CATALOG.some((entry) => entry.kind === tree.kind) ? tree.kind : "pine") as TreeKind,
      slot: index
    }));
  }, [forestData]);

  const rangeTrees = forestData?.trees ?? 0;
  const rangeMinutes = forestData?.minutes ?? 0;

  // Growth stage per species from session counts in the selected range (existing data, no migration).
  const forestTrees = useMemo<ForestTree[]>(() => {
    const sessionsByKind = new Map<string, number>();
    for (const day of forestData?.days ?? []) {
      for (const tree of day.trees) sessionsByKind.set(tree.kind, (sessionsByKind.get(tree.kind) ?? 0) + tree.count);
    }
    return planted.map((tree) => ({
      slot: tree.slot,
      kind: tree.kind,
      stage: stageForSessions(Math.max(1, sessionsByKind.get(tree.kind) ?? 1))
    }));
  }, [planted, forestData]);

  // Push re-derived trees into the mounted scene (data usually lands AFTER init).
  useEffect(() => {
    if (forestMode !== "3d") return;
    forest3dRef.current?.setTrees(forestTrees);
  }, [forestTrees, forestMode]);

  // Lazy-load the 3D scene after the page is interactive (own chunk, three never hits the main bundle).
  useEffect(() => {
    let cancelled = false;
    if (!webglSupported()) return; // CSS island stays
    const schedule = (cb: () => void): number =>
      typeof window.requestIdleCallback === "function" ? window.requestIdleCallback(() => cb()) : window.setTimeout(cb, 600);
    const unschedule = (id: number): void => {
      if (typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
    const id = schedule(() => {
      if (cancelled) return;
      import("../forest3d/forestScene")
        .then(({ mountForest3d }) => {
          if (cancelled || !canvasHostRef.current) return;
          forest3dRef.current = mountForest3d(canvasHostRef.current, {
            trees: forestTreesRef.current,
            onFallback: () => setForestMode("css") // WebGL unavailable / context lost
          });
          setForestMode("3d");
        })
        .catch(() => setForestMode("css"));
    });
    return () => {
      cancelled = true;
      unschedule(id);
    };
  }, []);

  useEffect(() => () => {
    forest3dRef.current?.dispose();
    forest3dRef.current = null;
  }, []);

  useEffect(() => {
    forestTreesRef.current = forestTrees;
    if (forestMode === "3d") forest3dRef.current?.setTrees(forestTrees);
  }, [forestTrees, forestMode]);

  // Session-completed hook — the existing trigger point, new visual payoff.
  useEffect(() => {
    if (focus.lastSavedAt && forestMode === "3d") forest3dRef.current?.celebrate();
  }, [focus.lastSavedAt, forestMode]);

  const selectedTree = TREE_CATALOG.find((tree) => tree.kind === treeKind) ?? TREE_CATALOG[0];
  const chooseTree = (kind: TreeKind) => {
    setTreeKind(kind);
    storeSelectedTree(kind);
  };

  const focusSound = (option: "forest" | "rain" | "waves" | "wind" | "off") =>
    option === "off" ? !focus.ambienceEnabled : focus.ambienceEnabled && focus.ambienceKind === option;

  const applySound = (option: "forest" | "rain" | "waves" | "wind" | "off") => {
    if (option === "off") {
      focus.setAmbienceEnabled(false);
      return;
    }
    focus.setAmbienceEnabled(true);
    focus.setAmbienceKind(option as AmbienceKind);
  };

  return (
    <div className="page-stack rise">
      <PageHeader title={t("focus.title")} subtitle={t("focus.subtitle")} />
      <div className="focus-grid">
        <Card className="focus-timer" elevated>
          <div className={`focus-orbit ${focus.running && !focus.paused ? "is-running" : ""}`} style={{ "--progress": `${Math.round(progress * 360)}deg` } as React.CSSProperties}>
            <div className="focus-orbit__inner">
              <strong>{mm}:{ss}</strong>
              <small>{!focus.running ? t("focus.duration") : focus.paused ? t("focus.paused") : t("focus.running")}</small>
            </div>
          </div>
          {!focus.running ? (
            <>
              <Field label={t("focus.goalLabel")} value={goal} onChange={(event) => setGoal(event.target.value)} placeholder={t("focus.goalPlaceholder")} />
              <SelectField label={t("course.title")} value={courseId} onChange={(event) => setCourseId(event.target.value)}>
                {courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
              </SelectField>
              <div className="duration-stepper" role="group" aria-label={t("focus.duration")}>
                <button type="button" onClick={() => changeDuration(-5)} disabled={duration <= MIN_MINUTES} aria-label={t("focus.decrease")}>−{formatNumber(5)}</button>
                <div className="duration-stepper__value">
                  <strong>{formatNumber(duration)}</strong>
                  <small>{t("common.minutes")}</small>
                </div>
                <button type="button" onClick={() => changeDuration(5)} disabled={duration >= MAX_MINUTES} aria-label={t("focus.increase")}>+{formatNumber(5)}</button>
              </div>
              <div className="duration-pills">
                {presets.map((value) => <button key={value} className={duration === value ? "is-active" : ""} onClick={() => setDuration(value)}>{formatNumber(value)}</button>)}
              </div>
              <div className="focus-actions">
                <Button icon="play" onClick={() => { rememberDuration(duration); focus.start(duration, courseId, goal.trim(), treeKind); }}>{t("focus.start")}</Button>
                {savedNotice && <Badge tone="green">{t("focus.treeGrown")}</Badge>}
              </div>
            </>
          ) : (
            <div className="focus-live">
              <span className="focus-live__goal">{focus.goal || t("focus.title")}</span>
              <div className="focus-sound" role="group" aria-label={t("focus.sound")}>
                {(["forest", "rain", "waves", "wind", "off"] as const).map((option) => (
                  <button key={option} className={`focus-sound__pill ${option === "off" ? "focus-sound__pill--off" : ""} ${focusSound(option) ? "is-active" : ""}`} onClick={() => applySound(option)}>
                    {t(`focus.sound_${option}`)}
                  </button>
                ))}
              </div>
              <div className="focus-actions">
                <Button variant="secondary" icon={focus.paused ? "play" : "pause"} onClick={() => (focus.paused ? focus.resume() : focus.pause())}>{focus.paused ? t("focus.resume") : t("focus.pause")}</Button>
                <Button icon="check" onClick={() => void focus.stopAndSave()}>{t("focus.finish")}</Button>
              </div>
              <small className="focus-live__hint">{t("focus.persistentHint")}</small>
            </div>
          )}
        </Card>

        <div className="forest-stage">
          <Card className="forest-card scene-card" elevated>
            <div
              className={`forest-scene forest-scene--day ${forestMode === "3d" ? "forest-scene--3d" : ""}`}
              ref={sceneRef}
              onMouseMove={forestMode === "css" ? onSceneMove : undefined}
              onMouseLeave={forestMode === "css" ? onSceneLeave : undefined}
            >
              {forestMode === "css" && (
                <>
                  <div className="forest-skyline"><i /><i /><i /></div>
                  <div className="island" aria-hidden="true">
                    <div className="island__soil" />
                    <div className="island__grass" />
                    <div className="island__slots">
                      {Array.from({ length: 12 }, (_, slot) => {
                        const tree = planted.find((item) => item.slot === slot);
                        return (
                          <span className={`island__slot slot-${slot}`} key={slot}>
                            {tree && <Tree3D kind={tree.kind} delay={tree.slot * 90} />}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {/* True 3D forest lives here; hidden until mounted, CSS island above stays as fallback */}
              <div className={`forest-canvas ${forestMode === "3d" ? "" : "forest-canvas--pending"}`} ref={canvasHostRef} aria-hidden="true" />
              <div className="forest-range" role="tablist" aria-label={t("focus.forestRange")}>
                {(["day", "week", "month", "term"] as const).map((option) => (
                  <button key={option} role="tab" aria-selected={forestRange === option} className={forestRange === option ? "is-active" : ""} onClick={() => setForestRange(option)}>
                    {t(`focus.f.${option}`)}
                  </button>
                ))}
              </div>
            </div>
            {/* Below the forest on touch devices; overlays the scene on desktop. */}
            <div className="forest-counter">
              <span className="section-kicker">{t("focus.forest")}</span>
              <h2>{t("focus.treeCount", { count: formatNumber(rangeTrees) })}</h2>
              <p>{rangeTrees ? t("focus.forestSummary", { minutes: formatNumber(rangeMinutes) }) : t("focus.forestEmptyRange")}</p>
              {forestData && forestData.streak > 0 && <p className="forest-streak">🔥 {t("focus.streak", { count: formatNumber(forestData.streak) })}</p>}
              {forestData && forestData.streak === 0 && <p className="forest-streak forest-streak--zero">{t("focus.streakZero")}</p>}
            </div>
          </Card>
          <Card className="tree-picker">
            <div className="section-heading compact">
              <div><span className="section-kicker">{t("focus.nextTree")}</span><h3>{t("focus.pickTree")}</h3></div>
              <Badge tone="green">{selectedTree.emoji}</Badge>
            </div>
            <div className="tree-picker__list" role="radiogroup" aria-label={t("focus.pickTree")}>
              {TREE_CATALOG.map((tree) => {
                const unlocked = sessions >= tree.minSessions;
                return (
                  <button
                    key={tree.kind}
                    role="radio"
                    aria-checked={treeKind === tree.kind}
                    className={`tree-option ${treeKind === tree.kind ? "is-selected" : ""} ${unlocked ? "" : "is-locked"}`}
                    onClick={() => unlocked && chooseTree(tree.kind)}
                    title={unlocked ? t(`trees.${tree.kind}`) : t("focus.unlockAt", { count: formatNumber(tree.minSessions) })}
                  >
                    <span className="tree-option__icon">{tree.emoji}</span>
                    <strong>{t(`trees.${tree.kind}`)}</strong>
                    {!unlocked && <small><Icon name="lock" size={11} />{formatNumber(tree.minSessions)}</small>}
                  </button>
                );
              })}
            </div>
            <p className="tree-picker__hint">{t("focus.treeHint", { tree: t(`trees.${treeKind}`) })}</p>
          </Card>
        </div>
      </div>

      <FocusCharts stats={stats} />
    </div>
  );
}

/** One low-poly tree with layered canopy — pure CSS 3D. */
export function Tree3D({ kind, delay = 0, mini = false }: { kind: TreeKind; delay?: number; mini?: boolean }) {
  const cls = `tree3d tree3d--${kind} ${mini ? "tree3d--mini" : ""}`;
  if (kind === "birch") {
    return (
      <span className={cls} style={{ "--delay": `${delay}ms` } as React.CSSProperties}>
        <i className="tree3d__trunk" />
        <i className="tree3d__leaf leaf-a" />
        <i className="tree3d__leaf leaf-b" />
      </span>
    );
  }
  if (kind === "sakura" || kind === "maple") {
    return (
      <span className={cls} style={{ "--delay": `${delay}ms` } as React.CSSProperties}>
        <i className="tree3d__trunk" />
        <i className="tree3d__ball ball-a" />
        <i className="tree3d__ball ball-b" />
        <i className="tree3d__ball ball-c" />
      </span>
    );
  }
  return (
    <span className={cls} style={{ "--delay": `${delay}ms` } as React.CSSProperties}>
      <i className="tree3d__trunk" />
      <i className="tree3d__cone cone-a" />
      <i className="tree3d__cone cone-b" />
      <i className="tree3d__cone cone-c" />
    </span>
  );
}
